import React, { useCallback, useEffect, useState } from 'react';
import { Button, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { useNotify, useTranslate } from 'react-admin';
import { provenanceService, type ProvenanceSummary } from '../../../services/provenanceService';

const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return '–';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const apiError = (error: unknown, fallback: string): string => {
    const data = (error as { response?: { data?: { message?: string; error?: string; detail?: string } } })?.response?.data;
    return data?.message ?? data?.detail ?? (data?.error && !/^[a-z_]+$/.test(data.error) ? data.error : fallback);
};

/**
 * Herkunft und Freigabe (Tranche B4, #489): how the course came to be (job,
 * prompt version, engine, models, material hash), the latest quality score
 * and the trainer's sign-off with a warning when the content changed since.
 */
export const ProvenanceCard: React.FC<{ courseId: number; onChanged?: () => void }> = ({ courseId, onChanged }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [data, setData] = useState<ProvenanceSummary | null>(null);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setData(await provenanceService.get(courseId));
        } catch {
            setData(null);
        }
    }, [courseId]);

    useEffect(() => { void load(); }, [load]);

    const act = async (withdraw: boolean) => {
        setBusy(true);
        try {
            setData(withdraw ? await provenanceService.withdraw(courseId) : await provenanceService.signOff(courseId, note.trim() || undefined));
            notify(translate(withdraw ? 'provenance.withdrawn' : 'provenance.signedOff'), { type: 'success' });
            setNote('');
            onChanged?.();
        } catch (e) {
            notify(apiError(e, translate('provenance.actionError')), { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    if (!data) return null;
    const gen = data.generation;

    return (
        <section className="oa-cq-card" data-testid="provenance-card">
            <h2 className="oa-cq-card-title">
                <span className="oa-cq-icon"><VerifiedUserOutlined fontSize="small" /></span>
                {translate('provenance.title')}
                <Chip size="small" sx={{ ml: 'auto' }} icon={data.origin !== 'manual' ? <SmartToyOutlined /> : undefined} label={translate(`provenance.origin.${data.origin}`)} data-testid="provenance-origin" />
            </h2>

            {gen && (
                <div className="oa-cq-dims" style={{ marginTop: 0, marginBottom: 12 }}>
                    <div className="oa-cq-dim"><span>{translate('provenance.generatedBy')}</span><strong>{gen.generatedByName ?? (gen.generatedBy ? `#${gen.generatedBy}` : '–')} · {formatDate(gen.generatedAt)}</strong></div>
                    <div className="oa-cq-dim" data-testid="provenance-models"><span>{translate('provenance.model')}</span><strong>{gen.models.length > 0 ? gen.models.join(', ') : '–'}{gen.engine ? ` (${gen.engine})` : ''}</strong></div>
                    <div className="oa-cq-dim"><span>{translate('provenance.promptVersion')}</span><strong>{gen.promptVersion ?? '–'}</strong></div>
                    <div className="oa-cq-dim"><span>{translate('provenance.material')}</span><strong>{gen.sourceMaterialChars > 0 ? translate('provenance.materialInfo', { chars: gen.sourceMaterialChars, hash: gen.sourceMaterialHash?.slice(0, 12) ?? '' }) : translate('provenance.noMaterial')}</strong></div>
                    {gen.derivedFromCourseId && <div className="oa-cq-dim"><span>{translate('provenance.derivedFrom')}</span><strong>#{gen.derivedFromCourseId}</strong></div>}
                </div>
            )}

            {data.quality && (
                <Typography variant="body2" sx={{ mb: 1.5 }}>{translate('provenance.lastQuality', { score: data.quality.score ?? '–', date: formatDate(data.quality.completedAt) })}</Typography>
            )}

            {data.signOffRequired && !data.signOff && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 1 }} data-testid="provenance-gate">{translate('provenance.gateHint')}</Typography>
            )}

            <div className="oa-cq-item">
                {data.signOff ? (
                    <div data-testid="provenance-signed">
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: '#2e7d32' }}>
                            <CheckCircleOutline fontSize="small" />
                            {translate('provenance.signedBy', { name: data.signOff.byName ?? `#${data.signOff.by ?? '?'}`, date: formatDate(data.signOff.at) })}
                        </Typography>
                        {data.signOff.note && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>„{data.signOff.note}“</Typography>}
                        {data.signOff.changedSince && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, color: '#b26a00' }} data-testid="provenance-changed">
                                <WarningAmberOutlined fontSize="small" />{translate('provenance.changedSince')}
                            </Typography>
                        )}
                        <div className="oa-aig-actions" style={{ justifyContent: 'flex-end' }}>
                            {data.signOff.changedSince && <Button size="small" variant="contained" onClick={() => void act(false)} disabled={busy}>{translate('provenance.signOffAgain')}</Button>}
                            <Button size="small" onClick={() => void act(true)} disabled={busy} data-testid="provenance-withdraw">{translate('provenance.withdraw')}</Button>
                        </div>
                    </div>
                ) : (
                    <div data-testid="provenance-unsigned">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{translate('provenance.notSigned')}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('provenance.signHint')}</Typography>
                        <TextField fullWidth size="small" multiline minRows={2} label={translate('provenance.note')} value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 2000, 'aria-label': translate('provenance.note') }} />
                        <div className="oa-aig-actions" style={{ justifyContent: 'flex-end' }}>
                            <Button size="small" variant="contained" startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutline />} onClick={() => void act(false)} disabled={busy} data-testid="provenance-sign">{translate('provenance.signOff')}</Button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default ProvenanceCard;
